import { Link } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { TableCell, TableRow } from '@/components/ui/table'
import { formatPhone } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { type PipelineDeal, PipelinePageStage, PipelineSection } from '@/models/myPipeline'
import { salesLeadStatusColors, salesLeadStatusLabels } from '@/models/salesLead'
import { InlineEditField } from '../SalesLeads/InlineEditField'
import { CloseMonthSelect } from './CloseMonthSelect'
import { carriedFromLabel, DealValueCell } from './DealValueCell'
import { NextActionDueCell } from './NextActionDueCell'
import { PipelineRowMenu } from './PipelineRowMenu'

function CompanyCell({ deal }: { deal: PipelineDeal }) {
  return (
    <div className={cn(deal.isEndorsement && 'pl-6')}>
      <div className="flex items-center gap-2">
        <Link to={`/insure/internal/sales-leads/${deal.leadId}`} className="font-medium hover:underline">
          {deal.legalName}
        </Link>
        {deal.isEndorsement && (
          <Badge variant="outline" className="text-xs">
            Endorsement
          </Badge>
        )}
      </div>
      <div className="text-muted-foreground text-xs">
        {deal.dotNumber ? `DOT ${deal.dotNumber}` : 'No DOT'}
        {deal.carriedFrom && ` · ${carriedFromLabel(deal.carriedFrom)}`}
        {!deal.isEndorsement && deal.pipelineSection === PipelineSection.AUTO && ' · auto'}
      </div>
    </div>
  )
}

function RowMenu({ deal }: { deal: PipelineDeal }) {
  if (deal.isEndorsement || deal.heldByPolicy) return null
  return (
    <PipelineRowMenu
      leadId={deal.leadId}
      legalName={deal.legalName}
      shownAs={PipelineSection.DEAL}
      pipelineSection={deal.pipelineSection}
    />
  )
}

function StageCell({ deal }: { deal: PipelineDeal }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge className={salesLeadStatusColors[deal.status]}>{salesLeadStatusLabels[deal.status]}</Badge>
      {/* A closed deal belongs to the month it closed, so only open deals can be moved. */}
      {deal.pageStage === PipelinePageStage.NEGOTIATION && !deal.isEndorsement && deal.expectedCloseMonth && (
        <CloseMonthSelect leadId={deal.leadId} month={deal.expectedCloseMonth} />
      )}
    </div>
  )
}

function ContactCell({ deal }: { deal: PipelineDeal }) {
  if (!deal.primaryContact) return <span className="text-muted-foreground text-sm">—</span>
  return (
    <div className="text-sm">
      <div>{deal.primaryContact.name || '—'}</div>
      <div className="text-muted-foreground text-xs">
        {formatPhone(deal.primaryContact.phone) || deal.primaryContact.email}
      </div>
    </div>
  )
}

export function DealRow({ deal, canEditDueDates }: { deal: PipelineDeal; canEditDueDates: boolean }) {
  return (
    <TableRow className={cn(deal.pageStage === PipelinePageStage.LOST && 'opacity-75')}>
      <TableCell>
        <CompanyCell deal={deal} />
      </TableCell>
      <TableCell>
        <ContactCell deal={deal} />
      </TableCell>
      <TableCell className="text-right">
        <DealValueCell deal={deal} />
      </TableCell>
      <TableCell>
        <StageCell deal={deal} />
      </TableCell>
      <TableCell>
        {deal.isEndorsement ? (
          <span className="text-muted-foreground text-sm">{deal.nextAction || '—'}</span>
        ) : (
          <InlineEditField
            leadId={deal.leadId}
            field="nextAction"
            value={deal.nextAction}
            placeholder="Add next action"
          />
        )}
      </TableCell>
      <TableCell>
        <NextActionDueCell
          leadId={deal.leadId}
          dueDate={deal.nextActionDate}
          overdue={deal.nextActionOverdue}
          readOnly={!canEditDueDates}
        />
      </TableCell>
      <TableCell className="text-right">
        <RowMenu deal={deal} />
      </TableCell>
    </TableRow>
  )
}

/** Below lg the table collapses to one card per deal. */
export function CompactDealCard({ deal, canEditDueDates }: { deal: PipelineDeal; canEditDueDates: boolean }) {
  return (
    <div className={cn('rounded-lg border p-3', deal.pageStage === PipelinePageStage.LOST && 'opacity-75')}>
      <div className="flex items-start justify-between gap-3">
        <CompanyCell deal={deal} />
        <div className="flex items-start gap-1">
          <DealValueCell deal={deal} />
          <RowMenu deal={deal} />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <StageCell deal={deal} />
        <NextActionDueCell
          leadId={deal.leadId}
          dueDate={deal.nextActionDate}
          overdue={deal.nextActionOverdue}
          readOnly={!canEditDueDates}
        />
      </div>
      <div className="mt-2">
        {deal.isEndorsement ? (
          <span className="text-muted-foreground text-sm">{deal.nextAction || '—'}</span>
        ) : (
          <InlineEditField
            leadId={deal.leadId}
            field="nextAction"
            value={deal.nextAction}
            placeholder="Add next action"
          />
        )}
      </div>
    </div>
  )
}
